import { addDays } from "./recurrence.ts";
import { TIMELINE_DAYS, validTimelineDate } from "./collection-timeline.ts";

export function timelineDateAt(start: string, x: number, left: number, width: number): string {
  return addDays(start,Math.max(0,Math.min(TIMELINE_DAYS-1,Math.floor((x-left)/Math.max(1,width/TIMELINE_DAYS)))));
}

interface Capabilities {
  canSchedule(id: string): boolean;
  schedule(id: string, start?: string, due?: string | null): void;
  canCreate: boolean;
  create(date: string): Promise<void>;
  rememberOpen(open: boolean): void;
}

/** Sparse grid controls: mouse placement/ranges, native date-form alternatives for touch/keyboard. */
export function bindTimelineEmptyGrid(section: HTMLElement, c: Capabilities): void {
  const start=section.dataset.timelineWindowStart,anchor=section.dataset.timelineAnchor;
  if(!validTimelineDate(start)||!validTimelineDate(anchor))return;
  const dateAt=(track:HTMLElement,x:number)=>{const bounds=track.getBoundingClientRect();return timelineDateAt(start!,x,bounds.left,bounds.width);};
  const group=section.querySelector<HTMLDetailsElement>("[data-timeline-unscheduled]");
  let open=group?.open;
  group?.addEventListener("toggle",()=>{if(group.open!==open){open=group.open;c.rememberOpen(group.open);}});
  section.querySelectorAll<HTMLButtonElement>("[data-timeline-schedule]").forEach(track=>{
    const id=track.dataset.timelineSchedule!;
    if(!c.canSchedule(id)){track.disabled=true;track.querySelector("span")!.textContent="No dates set";return;}
    let drag:{pointer:number;start:string;end:string;x:number;active:boolean}|null=null;
    let suppressedUntil=0,touch=false,cancelled=false;
    const clear=()=>{drag=null;track.classList.remove("is-schedule-drag");track.style.removeProperty("--schedule-left");track.style.removeProperty("--schedule-width");};
    track.addEventListener("click",event=>{event.preventDefault();event.stopPropagation();if(cancelled){cancelled=false;return;}if(performance.now()<suppressedUntil)return;
      if(!c.canSchedule(id))return;
      if(event.detail===0||touch)c.schedule(id);else c.schedule(id,dateAt(track,event.clientX),null);
      touch=false;
    });
    track.addEventListener("pointerdown",event=>{cancelled=false;touch=event.pointerType==="touch";if(event.button!==0||touch||!c.canSchedule(id))return;const date=dateAt(track,event.clientX);drag={pointer:event.pointerId,start:date,end:date,x:event.clientX,active:false};});
    track.addEventListener("pointermove",event=>{
      if(!drag||drag.pointer!==event.pointerId||!(event.buttons&1))return;
      if(!c.canSchedule(id)){clear();return;}
      if(!drag.active&&Math.abs(event.clientX-drag.x)<6)return;
      event.preventDefault();if(!drag.active){drag.active=true;track.setPointerCapture(event.pointerId);}
      drag.end=dateAt(track,event.clientX);track.classList.add("is-schedule-drag");
      const low=drag.start<drag.end?drag.start:drag.end,high=drag.start>drag.end?drag.start:drag.end;
      const offset=(Date.parse(low)-Date.parse(start!))/86400000,days=(Date.parse(high)-Date.parse(low))/86400000+1;
      track.style.setProperty("--schedule-left",`${offset/TIMELINE_DAYS*100}%`);track.style.setProperty("--schedule-width",`${days/TIMELINE_DAYS*100}%`);
    });
    track.addEventListener("pointerup",event=>{
      if(!drag||drag.pointer!==event.pointerId)return;const captured=drag;clear();
      if(!captured.active)return;
      suppressedUntil=performance.now()+300;if(track.hasPointerCapture(event.pointerId))track.releasePointerCapture(event.pointerId);
      if(c.canSchedule(id)){const low=captured.start<captured.end?captured.start:captured.end,high=captured.start>captured.end?captured.start:captured.end;c.schedule(id,low,low===high?null:high);}
    });
    const cancel=()=>{if(drag?.active){suppressedUntil=performance.now()+300;cancelled=true;}clear();};
    track.addEventListener("pointercancel",cancel);track.addEventListener("lostpointercapture",()=>{if(drag)cancel();});
    track.addEventListener("keydown",event=>{if(event.key==="Enter"||event.key===" ")event.stopPropagation();if(event.key==="Escape"&&drag){event.preventDefault();event.stopPropagation();cancel();}});
  });
  const row=section.querySelector<HTMLElement>("[data-timeline-create-row]");if(row)row.hidden=!c.canCreate;
  section.querySelector<HTMLButtonElement>("[data-timeline-new]")?.addEventListener("click",()=>{if(c.canCreate)void c.create(anchor!);});
  section.querySelector<HTMLButtonElement>("[data-timeline-create-grid]")?.addEventListener("click",event=>{event.preventDefault();event.stopPropagation();if(c.canCreate)void c.create(event.detail?dateAt(event.currentTarget as HTMLElement,event.clientX):anchor!);});
}
